const SESSION_KEY_PREFIX = 'grok-imagine:workbench-draft:';
const DATABASE_NAME = 'grok-imagine-workbench-drafts';
const DATABASE_VERSION = 1;
const FILES_STORE_NAME = 'files';
const FILE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const TOKEN_CLAIM_CHANNEL = 'grok-imagine-workbench-draft-claims';
const TOKEN_CLAIM_WAIT_MS = 40;

type TokenClaimStatus = 'provisional' | 'settled';

interface TokenClaimMessage {
  instanceId: string;
  requestId: string;
  status?: TokenClaimStatus;
  token: string;
  type: 'probe' | 'response';
}

const claimInstanceId = createToken();
const tokenClaims = new Map<string, TokenClaimStatus>();
let claimChannel: BroadcastChannel | null | undefined;

export type WorkbenchDraftFiles = Record<string, File | null>;

export interface RestoredWorkbenchDraft<T> {
  files: Record<string, File>;
  payload: T;
  token: string;
}

interface WorkbenchDraftEnvelope<T> {
  createdAt: number;
  fileSlots: string[];
  key: string;
  payload: T;
  token: string;
  updatedAt: number;
  version: number;
}

interface WorkbenchDraftFileRecord {
  file: File;
  slot: string;
  token: string;
  updatedAt: number;
}

function getSessionKey(key: string) {
  return `${SESSION_KEY_PREFIX}${key}`;
}

function getSessionStorage(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function getIndexedDb(): IDBFactory | null {
  try {
    return globalThis.indexedDB ?? null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isWorkbenchDraftEnvelope<T>(
  value: unknown
): value is WorkbenchDraftEnvelope<T> {
  return (
    isRecord(value) &&
    isFiniteNumber(value.createdAt) &&
    Array.isArray(value.fileSlots) &&
    value.fileSlots.every((slot) => typeof slot === 'string') &&
    typeof value.key === 'string' &&
    Object.hasOwn(value, 'payload') &&
    typeof value.token === 'string' &&
    value.token.length > 0 &&
    isFiniteNumber(value.updatedAt) &&
    isFiniteNumber(value.version)
  );
}

function parseWorkbenchDraftEnvelope<T>(
  value: string | null
): WorkbenchDraftEnvelope<T> | null {
  if (!value) return null;

  try {
    const parsed: unknown = JSON.parse(value);
    return isWorkbenchDraftEnvelope<T>(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readStoredEnvelope<T>(key: string) {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    const envelope = parseWorkbenchDraftEnvelope<T>(
      storage.getItem(getSessionKey(key))
    );
    return envelope?.key === key ? envelope : null;
  } catch {
    return null;
  }
}

function createToken() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a token that works in older browser environments.
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isTokenClaimMessage(value: unknown): value is TokenClaimMessage {
  return (
    isRecord(value) &&
    typeof value.instanceId === 'string' &&
    typeof value.requestId === 'string' &&
    typeof value.token === 'string' &&
    (value.type === 'probe' || value.type === 'response') &&
    (value.status === undefined ||
      value.status === 'provisional' ||
      value.status === 'settled')
  );
}

function getClaimChannel() {
  if (claimChannel !== undefined) return claimChannel;
  if (
    typeof window === 'undefined' ||
    typeof BroadcastChannel === 'undefined'
  ) {
    claimChannel = null;
    return claimChannel;
  }

  try {
    claimChannel = new BroadcastChannel(TOKEN_CLAIM_CHANNEL);
    claimChannel.addEventListener('message', (event) => {
      const message = event.data as unknown;
      if (!isTokenClaimMessage(message) || message.type !== 'probe') return;
      const status = tokenClaims.get(message.token);
      if (!status) return;

      claimChannel?.postMessage({
        instanceId: claimInstanceId,
        requestId: message.requestId,
        status,
        token: message.token,
        type: 'response',
      } satisfies TokenClaimMessage);
    });
  } catch {
    claimChannel = null;
  }

  return claimChannel;
}

async function claimToken(token: string) {
  if (tokenClaims.get(token) === 'settled') return true;
  const channel = getClaimChannel();
  if (!channel) {
    tokenClaims.set(token, 'settled');
    return true;
  }

  tokenClaims.set(token, 'provisional');
  const requestId = createToken();
  let losesClaim = false;
  const handleMessage = (event: MessageEvent<unknown>) => {
    const message = event.data;
    if (
      !isTokenClaimMessage(message) ||
      message.type !== 'response' ||
      message.requestId !== requestId ||
      message.token !== token ||
      message.instanceId === claimInstanceId
    ) {
      return;
    }

    losesClaim ||=
      message.status === 'settled' || message.instanceId < claimInstanceId;
  };

  channel.addEventListener('message', handleMessage);
  channel.postMessage({
    instanceId: claimInstanceId,
    requestId,
    token,
    type: 'probe',
  } satisfies TokenClaimMessage);
  await new Promise<void>((resolve) =>
    setTimeout(resolve, TOKEN_CLAIM_WAIT_MS)
  );
  channel.removeEventListener('message', handleMessage);

  if (losesClaim) {
    tokenClaims.delete(token);
    return false;
  }
  tokenClaims.set(token, 'settled');
  return true;
}

function waitForTransaction(transaction: IDBTransaction) {
  return new Promise<void>((resolve) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => resolve();
    transaction.onerror = () => resolve();
  });
}

async function removeExpiredFiles(database: IDBDatabase) {
  try {
    const transaction = database.transaction(FILES_STORE_NAME, 'readwrite');
    const completion = waitForTransaction(transaction);
    const request = transaction.objectStore(FILES_STORE_NAME).openCursor();
    const cutoff = Date.now() - FILE_MAX_AGE_MS;

    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;

      const record = cursor.value as unknown;
      if (
        isRecord(record) &&
        isFiniteNumber(record.updatedAt) &&
        record.updatedAt < cutoff
      ) {
        cursor.delete();
      }
      cursor.continue();
    };

    await completion;
  } catch {
    // Draft cleanup must never affect the primary workflow.
  }
}

function openDatabase(): Promise<IDBDatabase | null> {
  const indexedDatabase = getIndexedDb();
  if (!indexedDatabase) return Promise.resolve(null);

  return new Promise((resolve) => {
    try {
      const request = indexedDatabase.open(DATABASE_NAME, DATABASE_VERSION);

      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(FILES_STORE_NAME)) {
          database.createObjectStore(FILES_STORE_NAME, {
            keyPath: ['token', 'slot'],
          });
        }
      };
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
      request.onsuccess = () => {
        const database = request.result;
        void removeExpiredFiles(database).then(() => resolve(database));
      };
    } catch {
      resolve(null);
    }
  });
}

function isWorkbenchDraftFileRecord(
  value: unknown,
  token: string,
  slot: string
): value is WorkbenchDraftFileRecord {
  return (
    isRecord(value) &&
    value.token === token &&
    value.slot === slot &&
    isFiniteNumber(value.updatedAt) &&
    typeof File !== 'undefined' &&
    value.file instanceof File
  );
}

function readFileRecord(store: IDBObjectStore, token: string, slot: string) {
  return new Promise<WorkbenchDraftFileRecord | null>((resolve) => {
    try {
      const request = store.get([token, slot]);
      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const value = request.result as unknown;
        resolve(isWorkbenchDraftFileRecord(value, token, slot) ? value : null);
      };
    } catch {
      resolve(null);
    }
  });
}

async function writeFiles(
  token: string,
  files: WorkbenchDraftFiles,
  removedSlots: string[],
  updatedAt: number
) {
  const database = await openDatabase();
  if (!database) return;

  try {
    const transaction = database.transaction(FILES_STORE_NAME, 'readwrite');
    const completion = waitForTransaction(transaction);
    const store = transaction.objectStore(FILES_STORE_NAME);

    for (const slot of removedSlots) {
      store.delete([token, slot]);
    }
    for (const [slot, file] of Object.entries(files)) {
      if (!file) continue;
      store.put({ file, slot, token, updatedAt });
    }

    await completion;
  } catch {
    // File persistence is best effort only.
  } finally {
    database.close();
  }
}

async function readFiles(token: string, slots: string[]) {
  const files: Record<string, File> = {};
  if (slots.length === 0) return files;

  const database = await openDatabase();
  if (!database) return files;

  try {
    const transaction = database.transaction(FILES_STORE_NAME, 'readonly');
    const completion = waitForTransaction(transaction);
    const store = transaction.objectStore(FILES_STORE_NAME);
    const records = await Promise.all(
      slots.map((slot) => readFileRecord(store, token, slot))
    );

    for (const record of records) {
      if (record) files[record.slot] = record.file;
    }
    await completion;
  } catch {
    // Serializable draft restoration can continue without local files.
  } finally {
    database.close();
  }

  return files;
}

async function deleteFiles(token: string, slots: string[]) {
  if (slots.length === 0) return;

  const database = await openDatabase();
  if (!database) return;

  try {
    const transaction = database.transaction(FILES_STORE_NAME, 'readwrite');
    const completion = waitForTransaction(transaction);
    const store = transaction.objectStore(FILES_STORE_NAME);

    for (const slot of slots) {
      store.delete([token, slot]);
    }
    await completion;
  } catch {
    // Clearing the serializable draft must still succeed without IndexedDB.
  } finally {
    database.close();
  }
}

export async function writeWorkbenchDraft<T>(input: {
  files: WorkbenchDraftFiles;
  key: string;
  payload: T;
  version: number;
}): Promise<void> {
  const storage = getSessionStorage();
  if (!storage) return;

  const existing = readStoredEnvelope<unknown>(input.key);
  let token = existing?.token ?? createToken();
  if (!(await claimToken(token))) {
    token = createToken();
    await claimToken(token);
  }
  const fileSlots = Object.entries(input.files)
    .filter(([, file]) => file !== null)
    .map(([slot]) => slot);
  const now = Date.now();
  const envelope: WorkbenchDraftEnvelope<T> = {
    createdAt: existing?.createdAt ?? now,
    fileSlots,
    key: input.key,
    payload: input.payload,
    token,
    updatedAt: now,
    version: input.version,
  };

  try {
    storage.setItem(getSessionKey(input.key), JSON.stringify(envelope));
  } catch {
    return;
  }

  const currentSlots = new Set(fileSlots);
  const removedSlots =
    existing?.fileSlots.filter((slot) => !currentSlots.has(slot)) ?? [];
  if (fileSlots.length > 0 || removedSlots.length > 0) {
    await writeFiles(token, input.files, removedSlots, now);
  }
}

export async function readWorkbenchDraft<T>(input: {
  key: string;
  version: number;
}): Promise<RestoredWorkbenchDraft<T> | null> {
  const envelope = readStoredEnvelope<T>(input.key);
  if (!envelope || envelope.version !== input.version) return null;

  const inheritedToken = envelope.token;
  const files = await readFiles(inheritedToken, envelope.fileSlots);
  if (!(await claimToken(inheritedToken))) {
    const token = createToken();
    await claimToken(token);
    const updatedAt = Date.now();
    const forkedEnvelope = { ...envelope, token, updatedAt };
    const storage = getSessionStorage();
    try {
      storage?.setItem(
        getSessionKey(input.key),
        JSON.stringify(forkedEnvelope)
      );
    } catch {
      // The inherited serializable draft remains usable in memory.
    }
    await writeFiles(token, files, [], updatedAt);
    return { files, payload: envelope.payload, token };
  }

  return {
    files,
    payload: envelope.payload,
    token: inheritedToken,
  };
}

export async function clearWorkbenchDraft(key: string): Promise<void> {
  const envelope = readStoredEnvelope<unknown>(key);
  const storage = getSessionStorage();

  if (storage) {
    try {
      storage.removeItem(getSessionKey(key));
    } catch {
      // Clearing IndexedDB remains best effort when session storage fails.
    }
  }

  if (envelope && (await claimToken(envelope.token))) {
    await deleteFiles(envelope.token, envelope.fileSlots);
    tokenClaims.delete(envelope.token);
  }
}
